import { toast } from 'react-toastify';
import { AnyAction, type Middleware } from '@reduxjs/toolkit';

import { ErrorPayload } from '../lib/axiosBaseQuery';
import { env } from '../lib/env';

const handledActions = [
  `updateGroup`,
  `fetchGroups`,
  `addContactToGroup`,
  `removeContactFromGroup`,
  `searchContacts`,
  `saveWelcomeQuestions`, //Silent update
  `createSystemPolicy`,
  `adhocInterestCharge`,
  `adhocStatement`,
  `previewAction`,
  `previewPolicy`,
  `dismissWelcomeMessage`,
  // AI Agent reads/polls - never toast on these (they refetch frequently)
  `getAgentConversations`,
  `getAgentConversation`,
  `getToolApprovals`,
  `getAgentSettings`,
  `getAgentPrompts`,
  // AI Agent chat send handles notifications locally in UI
  `sendConversationMessage`,
  `sendAssistantMessage`,
  `newAssistantConversation`,
];

const handledSuccessOnly = [
  `updatePolicy`,
  `toggleContactFreeze`,
  `startMobileNumberCheck`, // Progress toast takes over
  `startMobileNumberFix`, // Modal shows progress
  `fixContactMobileNumbers`, // Modal shows the proposals
  `applyMobileNumberFix`, // Modal shows what was applied
  `systemPolicyUpdated`,
  `createPolicy`,
  `suggestPolicyTitle`,
  `sendStatementBuilderMessage`,
  `publishStatementBuilder`,
];

// GET requests never toast on success unless the endpoint is listed here
const getSuccessEnabled: string[] = [];

const successOverides = {
  [`syncContacts`]: `Contact sync has started. Please check back shortly.`,
  [`updateSettings`]: `Settings Updated`,
  [`saveNotificationSettings`]: `Notification settings updated`,
  [`uploadPOFile`]: `File uploaded successfully`,
};

function formatRetryDelay(seconds?: number): string {
  if (!seconds) {
    return `a moment`;
  }

  if (seconds < 120) {
    return `${seconds} seconds`;
  }

  if (seconds < 7200) {
    return `${Math.ceil(seconds / 60)} minutes`;
  }

  return `${Math.ceil(seconds / 3600)} hours`;
}

/** Local dev only: tag the toast with the endpoint that triggered it */
function withEndpoint(message: string, endpointName?: string) {
  return env.isLocal && endpointName ? `${message} [${endpointName}]` : message;
}

/** Custom middleware for display error toast - or success? */
export const rtkQueryActionLogger: Middleware = () => next => (action: AnyAction) => {
  // Find the rejected action type for api calls
  const regexError = /api\/(.*)\/rejected/;
  const regexSuccess = /api\/(.*)\/fulfilled/;

  const endpointName = action.meta?.arg?.endpointName;

  if (!handledActions.includes(endpointName)) {
    if (regexError.test(action.type)) {
      const errorPayload = action.payload as ErrorPayload;

      if (typeof errorPayload?.data === `string`) {
        errorPayload.data = {
          message: errorPayload.data,
        };
      }

      // Error is not being handled elsewhere, so we'll display it
      const errorMessage = errorPayload?.data?.message || `Unknown error occured`;

      // if (errorPayload?.status === 500) {
      //   // Display a generic message to user
      //   errorMessage = `An unknown error occured`;
      // }

      // else if (errorPayload?.status === 404) {
      //   errorMessage = `Not found`;
      // }

      // Skipped by condition, or aborted by RTK Query (e.g. cache entry removed
      // while the request was in flight) - neither is a real error
      if (action?.error?.name === `ConditionError` || action.meta?.aborted) {
        return next(action);
      }

      if (errorPayload?.status === 429) {
        // Fixed toastId so a burst of limited requests shows a single toast
        toast.error(withEndpoint(`Too many requests. Try again in ${formatRetryDelay(errorPayload.retryAfterSeconds)}.`, endpointName), {
          toastId: `rate-limited`,
        });

        return next(action);
      }

      // Can ignore this side effect error
      if (errorMessage !== `Aborted due to condition callback returning false.`) {
        toast.error(withEndpoint(errorMessage, endpointName), {
          toastId: errorMessage,
        });
      }
    }

    if (regexSuccess.test(action.type)) {
      const isGet = action.meta?.baseQueryMeta?.method === `GET`;

      if ((isGet && !getSuccessEnabled.includes(endpointName)) || handledSuccessOnly.includes(endpointName)) {
        return next(action);
      }

      if (action.meta?.arg?.originalArgs?.action === `resync`) {
        // Dont display success for resync
        return next(action);
      }

      if (action.payload?.warning) {
        toast.warn(withEndpoint(action.payload.warning, endpointName), {
          autoClose: 2500,
        });

        // Log for audit
        console.error(`Warning:`, action.payload.warning);

        return next(action);
      }

      if (action.payload?.success) {
        toast.success(withEndpoint(action.payload.success, endpointName), {
          autoClose: 4000,
        });

        return next(action);
      }

      // Display a generic success message
      const successMessage = successOverides[endpointName] || `Success!`;

      if (endpointName === `addContactGroup`) {
        const messages = [
          `${action.payload?.successCount} contacts added.`,
        ];

        if (action.payload?.alreadyExistsCount) {
          messages.push(`${action.payload?.alreadyExistsCount} contacts already exist in another group and where not added.`);
        }

        messages.forEach(message => {
          toast.success(withEndpoint(message, endpointName), {
            autoClose: 1500,
          });
        });
      }
      else {
        toast.success(withEndpoint(successMessage, endpointName), {
          autoClose: 1500,
        });
      }
    }
  }

  return next(action);
};
